import React from 'react';
import { motion } from 'framer-motion';
import { userTypes } from '../../../data/comprehensiveFindingsData';
import './UserTypesJourney.css';

const UserTypesJourney: React.FC = () => {
  // SVG viewBox dimensions
  const viewBoxWidth = 580;
  const viewBoxHeight = 400;

  // Path definitions for user journey flow
  const paths = [
    { from: 1, to: 2, d: 'M 80 200 L 200 120' }, // Not Aware → Interested
    { from: 1, to: 3, d: 'M 80 200 L 200 280' }, // Not Aware → Not Interested (fork)
    { from: 2, to: 4, d: 'M 200 120 L 340 120' }, // Interested → Aware but Not Using
    { from: 4, to: 5, d: 'M 340 120 L 550 120' }, // Aware but Not Using → Have Used Services (extends to card 5)
    { from: 6, to: 4, d: 'M 340 280 L 340 120' }, // Need Clear Info → Aware but Not Using (alternative path)
  ];

  return (
    <div className="tmf-user-journey">
      <div className="tmf-journey-container">
        <svg
          viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
          className="tmf-journey-svg"
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Animated connecting paths */}
          {paths.map((path, index) => (
            <motion.path
              key={`path-${index}`}
              d={path.d}
              stroke="rgba(253, 214, 42, 0.4)"
              strokeWidth="2"
              fill="none"
              strokeDasharray="5,5"
              initial={{ pathLength: 0, opacity: 0 }}
              whileInView={{ pathLength: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{
                duration: 1.5,
                delay: 0.3 + index * 0.2,
                ease: 'easeInOut',
              }}
            />
          ))}

          {/* Arrow markers for paths */}
          <defs>
            <marker
              id="arrowhead-yellow"
              markerWidth="10"
              markerHeight="10"
              refX="9"
              refY="3"
              orient="auto"
            >
              <polygon points="0 0, 10 3, 0 6" fill="rgba(253, 214, 42, 0.6)" />
            </marker>
          </defs>

          {/* User type nodes */}
          {userTypes.map((userType, index) => {
            // Skip rendering nodes for cards 1, 2, 3, 4, and 5 - only show node 6
            if (userType.id === 1 || userType.id === 2 || userType.id === 3 || userType.id === 4 || userType.id === 5) return null;

            return (
              <g key={userType.id}>
                {/* Node circle */}
                <motion.circle
                  cx={userType.position.x}
                  cy={userType.position.y}
                  r="8"
                  fill="#FDD62A"
                  initial={{ scale: 0, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.5,
                    delay: 0.5 + index * 0.15,
                    ease: 'backOut',
                  }}
                />

                {/* Node pulse effect */}
                <motion.circle
                  cx={userType.position.x}
                  cy={userType.position.y}
                  r="8"
                  fill="none"
                  stroke="#FDD62A"
                  strokeWidth="2"
                  initial={{ scale: 1, opacity: 0.6 }}
                  animate={{
                    scale: [1, 1.8, 1],
                    opacity: [0.6, 0, 0.6],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: 1 + index * 0.15,
                  }}
                />
              </g>
            );
          })}
        </svg>

        {/* User type cards positioned absolutely */}
        <div className="tmf-journey-cards">
          {userTypes.map((userType, index) => {
            // Calculate card position based on SVG coordinates
            const cardX = (userType.position.x / viewBoxWidth) * 100;
            const cardY = (userType.position.y / viewBoxHeight) * 100;

            return (
              <motion.div
                key={userType.id}
                className="tmf-journey-card"
                style={{
                  left: `${cardX}%`,
                  top: `${cardY}%`,
                }}
                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.6,
                  delay: 0.7 + index * 0.15,
                  ease: 'backOut',
                }}
                whileHover={{ scale: 1.05, y: -4 }}
              >
                <div className="tmf-journey-card-number">{userType.id}</div>
                <h4 className="tmf-journey-card-title">{userType.title}</h4>
                <p className="tmf-journey-card-description">{userType.description}</p>
                <span className="tmf-journey-card-theme">Theme {userType.themeRef}</span>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Journey legend */}
      <motion.div
        className="tmf-journey-legend"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 1.5 }}
      >
        <div className="tmf-journey-legend-item">
          <div className="tmf-journey-legend-line"></div>
          <span>User awareness progression</span>
        </div>
        <div className="tmf-journey-legend-item">
          <div className="tmf-journey-legend-dot"></div>
          <span>User type stage</span>
        </div>
      </motion.div>
    </div>
  );
};

export default UserTypesJourney;
